import {
  getStoredLoginIdentifier,
  getStoredLoginType,
} from '../auth/sessionToken';
import {
  loadPersistedLoginAttempt,
} from '../utils/authAttemptStorage';
import {
  readLastLoginOption,
  readStoredIdentifierForLoginOption,
} from './lastLoginStorage';
import { isLegacyAssetOwnerLoginType } from './assetOwnerApiPaths';

/**
 * Maps to the `C:\\Users\\donny\\testings\\login` Node scripts:
 * - `login/clients` — business client, private client, business worker
 * - `login/AssetOwner` — asset owners (role 4); legacy private/business owner labels still accepted
 */
export const LOGIN_OPTIONS = [
  'business client',
  'private client',
  'business worker',
  'asset owner',
  'business asset owner',
  'private asset owner',
] as const;

export type LoginOption = (typeof LOGIN_OPTIONS)[number];

/** Login types on the “other sign-in” page (canonical asset owner only in the picker). */
export const OTHER_SIGN_IN_LOGIN_OPTIONS = LOGIN_OPTIONS.filter(
  (option) => option !== 'private asset owner' && option !== 'business asset owner',
) as readonly LoginOption[];

type LoginTypeMeta = {
  path: string;
  emptyPayload: { identifier: string; password: string };
};

export const LOGIN_META: Record<LoginOption, LoginTypeMeta> = {
  'business client': {
    path: 'login/clients',
    emptyPayload: { identifier: '', password: '' },
  },
  'private client': {
    path: 'login/clients',
    emptyPayload: { identifier: '', password: '' },
  },
  'business worker': {
    path: 'login/clients',
    emptyPayload: { identifier: '', password: '' },
  },
  'asset owner': {
    path: 'login/assetsowners',
    emptyPayload: { identifier: '', password: '' },
  },
  'business asset owner': {
    path: 'login/assetsowners',
    emptyPayload: { identifier: '', password: '' },
  },
  'private asset owner': {
    path: 'login/assetsowners',
    emptyPayload: { identifier: '', password: '' },
  },
};

export function isLoginOption(raw: string): raw is LoginOption {
  return (LOGIN_OPTIONS as readonly string[]).includes(raw);
}

/** Map legacy asset-owner menu labels to the canonical login type for new API calls. */
export function canonicalAssetOwnerLoginType(loginType: LoginOption): LoginOption {
  return isLegacyAssetOwnerLoginType(loginType) ? 'asset owner' : loginType;
}

export function loginFormPayloadForOption(option: LoginOption): { identifier: string; password: string } {
  const attempt = loadPersistedLoginAttempt(option);
  if (attempt) {
    return attempt;
  }
  const rememberedIdentifier = readStoredIdentifierForLoginOption(option);
  if (rememberedIdentifier) {
    return { identifier: rememberedIdentifier, password: '' };
  }
  const sessionType = getStoredLoginType();
  const sessionId = getStoredLoginIdentifier();
  if (sessionType === option && sessionId?.trim()) {
    return { identifier: sessionId.trim(), password: '' };
  }
  return { ...LOGIN_META[option].emptyPayload };
}

export function getLastLoginState():
  | {
      option: LoginOption;
      identifier: string;
    }
  | null {
  const sessionType = getStoredLoginType();
  const sessionId = getStoredLoginIdentifier();
  if (sessionType && sessionId?.trim() && isLoginOption(sessionType)) {
    return { option: sessionType, identifier: sessionId.trim() };
  }

  const opt = readLastLoginOption(LOGIN_OPTIONS);
  if (!opt || !isLoginOption(opt)) {
    return null;
  }
  const id = readStoredIdentifierForLoginOption(opt);
  if (!id?.trim()) {
    return null;
  }
  return { option: opt, identifier: id.trim() };
}

export function getInitialLoginForm(): {
  option: LoginOption;
  payload: { identifier: string; password: string };
} {
  const last = getLastLoginState();
  if (last !== null) {
    return {
      option: last.option,
      payload: loginFormPayloadForOption(last.option),
    };
  }
  const remembered = readLastLoginOption(LOGIN_OPTIONS);
  if (remembered && isLoginOption(remembered)) {
    return { option: remembered, payload: loginFormPayloadForOption(remembered) };
  }
  const option: LoginOption = 'private client';
  return { option, payload: loginFormPayloadForOption(option) };
}
